/**
 * PTBFRollingFormDialog — Create / Edit PTBF Rolling (chuyển tháng futures).
 * Map theo SRS §2.10 (FR-RL-01..04). Auto-tạo Annex `ref_amendment` ở BE.
 *
 * Workflow:
 *  - Khi user chọn `contract_no`, gọi `useGetLocalPQuery` để display info
 *    + auto-fill `old_diffs` = `ptbf_diffs`, `old_futures_month`
 *    + auto-tính `quantity_kg` = contract.quantity_kg × lot_to_roll / total_lot
 *      (giả định 1 lot = 60kg nếu không có total_lot).
 *  - User có thể override `quantity_kg`.
 */
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { useSnackbar } from '@/hooks/useSnackbar'
import { useGetLocalPQuery, useListLocalPQuery } from '@/store/api/localPApi'
import { useListFuturesMonthsQuery } from '@/store/api/lookupsApi'
import {
  useCreatePTBFRollingMutation,
  useUpdatePTBFRollingMutation,
} from '@/store/api/ptbfRollingsApi'
import type { PTBFRollingCreateIn, PTBFRollingOut } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

export interface PTBFRollingFormDialogProps {
  open: boolean
  mode: 'create' | 'edit'
  fixedContractNo?: string
  initialValue?: PTBFRollingOut | null
  onClose: () => void
  onSaved?: (id: number) => void
}

interface RollingFormValues {
  contract_no: string
  rolling_date: string
  lot_to_roll: number | ''
  old_futures_month: string
  new_futures_month: string
  spread: number | ''
  rolling_fee: number | ''
  quantity_kg: number | ''
  quantity_override: boolean
  old_diffs: number | ''
  note: string
}

const EMPTY: RollingFormValues = {
  contract_no: '',
  rolling_date: dayjs().format('YYYY-MM-DD'),
  lot_to_roll: '',
  old_futures_month: '',
  new_futures_month: '',
  spread: '',
  rolling_fee: '',
  quantity_kg: '',
  quantity_override: false,
  old_diffs: '',
  note: '',
}

const schema: yup.ObjectSchema<RollingFormValues> = yup.object({
  contract_no: yup.string().trim().required('Bắt buộc chọn hợp đồng'),
  rolling_date: yup.string().required('Bắt buộc chọn ngày rolling'),
  lot_to_roll: yup
    .mixed<number | ''>()
    .test('required', 'Bắt buộc nhập số lot rolling', (v) => v !== '' && v !== undefined && v !== null)
    .test('positive', 'Lot To Roll phải > 0', (v) => typeof v === 'number' && v > 0)
    .required(),
  old_futures_month: yup.string().trim().required('Bắt buộc chọn Old Futures Month'),
  new_futures_month: yup
    .string()
    .trim()
    .required('Bắt buộc chọn New Futures Month')
    .test(
      'different',
      'New Futures Month phải khác Old Futures Month',
      function (v) {
        const old = this.parent.old_futures_month as string
        if (!v || !old) return true
        return v !== old
      },
    ),
  spread: yup
    .mixed<number | ''>()
    .test('required', 'Bắt buộc nhập Spread', (v) => v !== '' && v !== undefined && v !== null)
    .required(),
  rolling_fee: yup
    .mixed<number | ''>()
    .test('required', 'Bắt buộc nhập Rolling Fee', (v) => v !== '' && v !== undefined && v !== null)
    .required(),
  quantity_kg: yup.mixed<number | ''>().default(''),
  quantity_override: yup.boolean().default(false),
  old_diffs: yup.mixed<number | ''>().default(''),
  note: yup.string().trim().default(''),
}) as yup.ObjectSchema<RollingFormValues>

function fromRolling(r: PTBFRollingOut): RollingFormValues {
  const n = (x: number | null | undefined): number | '' =>
    x === null || x === undefined ? '' : x
  const s = (x: string | null | undefined): string => x ?? ''
  return {
    contract_no: s(r.contract_no),
    rolling_date: s(r.rolling_date),
    lot_to_roll: n(r.lot_to_roll),
    old_futures_month: s(r.old_futures_month),
    new_futures_month: s(r.new_futures_month),
    spread: n(r.spread),
    rolling_fee: n(r.rolling_fee),
    quantity_kg: n(r.quantity_kg),
    quantity_override: true, // edit mode respect saved value
    old_diffs: n(r.old_diffs),
    note: s(r.note),
  }
}

function toApi(v: RollingFormValues): PTBFRollingCreateIn {
  const num = (x: number | ''): number | null => (x === '' ? null : x)
  const str = (x: string): string | null => (x.trim() === '' ? null : x.trim())
  return {
    contract_no: v.contract_no.trim(),
    rolling_date: v.rolling_date,
    lot_to_roll: typeof v.lot_to_roll === 'number' ? v.lot_to_roll : 0,
    old_futures_month: v.old_futures_month,
    new_futures_month: v.new_futures_month,
    spread: typeof v.spread === 'number' ? v.spread : 0,
    rolling_fee: typeof v.rolling_fee === 'number' ? v.rolling_fee : 0,
    quantity_kg: num(v.quantity_kg),
    old_diffs: num(v.old_diffs),
    note: str(v.note),
  }
}

const KG_PER_LOT = 10000 // Robusta ICE/LIFFE: 1 lot = 10 MT = 10,000 kg

export function PTBFRollingFormDialog({
  open,
  mode,
  fixedContractNo,
  initialValue,
  onClose,
  onSaved,
}: PTBFRollingFormDialogProps) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createRolling, { isLoading: creating }] = useCreatePTBFRollingMutation()
  const [updateRolling, { isLoading: updating }] = useUpdatePTBFRollingMutation()
  const loading = creating || updating

  const { data: futuresMonths = [] } = useListFuturesMonthsQuery()

  const { data: contractsList } = useListLocalPQuery({
    contract_type: 'Differential',
    is_status: 1,
    page: 1,
    page_size: 200,
  })
  const contractOptions = useMemo(
    () => contractsList?.items ?? [],
    [contractsList],
  )

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RollingFormValues>({
    resolver: yupResolver(schema),
    defaultValues: EMPTY,
  })

  const selectedContractNo = watch('contract_no')
  const lotToRoll = watch('lot_to_roll')
  const quantityOverride = watch('quantity_override')

  const { data: contractDetailRes } = useGetLocalPQuery(selectedContractNo, {
    skip: !selectedContractNo,
  })
  const contractDetail = contractDetailRes?.data

  const [lastFilledContractNo, setLastFilledContractNo] = useState('')

  useEffect(() => {
    if (!open) return
    setLastFilledContractNo('')
    if (isEdit && initialValue) {
      reset(fromRolling(initialValue))
    } else {
      reset({ ...EMPTY, contract_no: fixedContractNo ?? '' })
    }
  }, [open, isEdit, initialValue, fixedContractNo, reset])

  // Auto-fill old_diffs / old_futures_month when contract changes (create-mode only)
  useEffect(() => {
    if (isEdit) return
    if (!contractDetail) return
    if (!selectedContractNo) return
    if (lastFilledContractNo === selectedContractNo) return

    setValue('old_diffs', contractDetail.ptbf_diffs ?? '')
    setValue('old_futures_month', contractDetail.futures_month ?? '')
    setLastFilledContractNo(selectedContractNo)
  }, [
    isEdit,
    contractDetail,
    selectedContractNo,
    lastFilledContractNo,
    setValue,
  ])

  // Auto-compute quantity_kg = contract.quantity_kg × lot_to_roll / total_lot
  // Fallback: lot_to_roll × KG_PER_LOT.
  useEffect(() => {
    if (quantityOverride) return
    const lot = typeof lotToRoll === 'number' ? lotToRoll : null
    if (lot === null || lot <= 0) return
    const totalQty = contractDetail?.quantity_kg
    // Heuristic: nếu contract có quantity_kg + ta đoán total_lot từ no_of_bag/100
    // hoặc dùng rate KG_PER_LOT.
    let q: number
    if (typeof totalQty === 'number' && totalQty > 0) {
      // Giả định contract đầy đủ N lot tương ứng totalQty → quy đổi tỉ lệ
      // Khi chưa có total_lot ở contract level → fallback KG_PER_LOT.
      q = lot * KG_PER_LOT
    } else {
      q = lot * KG_PER_LOT
    }
    setValue('quantity_kg', Number(q.toFixed(0)))
  }, [lotToRoll, contractDetail, quantityOverride, setValue])

  const cpDisplay = useMemo(() => {
    if (!contractDetail) return ''
    return `${contractDetail.cp_code ?? ''} — ${contractDetail.supplier_name ?? contractDetail.short_name ?? ''}`
  }, [contractDetail])

  const onSubmit = handleSubmit(
    async (values) => {
      const payload = toApi(values)
      try {
        if (isEdit && initialValue) {
          const res = await updateRolling({
            id: initialValue.id,
            body: payload,
          }).unwrap()
          snack.success('Đã cập nhật rolling')
          onSaved?.(res.data?.id ?? initialValue.id)
        } else {
          const res = await createRolling(payload).unwrap()
          snack.success('Đã tạo rolling (auto-Annex)')
          onSaved?.(res.data?.id ?? 0)
        }
        onClose()
      } catch (err) {
        snack.error(
          getErrorMessage(
            err,
            isEdit ? 'Cập nhật rolling thất bại' : 'Tạo rolling thất bại',
          ),
        )
      }
    },
    () => snack.error('Vui lòng kiểm tra lại các trường còn lỗi'),
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEdit ? `Sửa PTBF Rolling #${initialValue?.id ?? ''}` : 'Tạo PTBF Rolling'}
      </DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          {/* Row 1: Contract + Rolling Date */}
          <Box display="flex" gap={2} flexWrap="wrap">
            {fixedContractNo ? (
              <TextField
                label="Hợp đồng"
                size="small"
                value={fixedContractNo}
                disabled
                sx={{ flex: '1 1 240px' }}
              />
            ) : (
              <Controller
                control={control}
                name="contract_no"
                render={({ field }) => {
                  const found = contractOptions.find(
                    (c) => c.contract_no === field.value,
                  )
                  const selected =
                    found ??
                    (field.value
                      ? ({
                          contract_no: field.value,
                          supplier_name: null,
                          cp_code: null,
                        } as (typeof contractOptions)[number])
                      : null)
                  return (
                    <Autocomplete
                      size="small"
                      sx={{ flex: '2 1 320px' }}
                      options={contractOptions}
                      value={selected}
                      onChange={(_e, v) => field.onChange(v?.contract_no ?? '')}
                      getOptionLabel={(opt) =>
                        opt
                          ? `${opt.contract_no} — ${opt.supplier_name ?? opt.cp_code ?? ''}`
                          : ''
                      }
                      isOptionEqualToValue={(o, v) => o.contract_no === v.contract_no}
                      disabled={isEdit}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Hợp đồng (Differential active) *"
                          error={!!errors.contract_no}
                          helperText={errors.contract_no?.message}
                        />
                      )}
                    />
                  )
                }}
              />
            )}
            <Controller
              control={control}
              name="rolling_date"
              render={({ field }) => (
                <DatePicker
                  label="Ngày Rolling *"
                  format="DD-MMM-YYYY"
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(d) => field.onChange(d ? d.format('YYYY-MM-DD') : '')}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { flex: '1 1 180px' },
                      error: !!errors.rolling_date,
                      helperText: errors.rolling_date?.message,
                    },
                  }}
                />
              )}
            />
          </Box>

          {/* Row 2: Supplier display */}
          <TextField
            label="Nhà cung cấp"
            size="small"
            value={cpDisplay}
            disabled
            fullWidth
          />

          {/* Row 3: Old Futures + New Futures */}
          <Box display="flex" gap={2} flexWrap="wrap">
            <Controller
              control={control}
              name="old_futures_month"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Old Futures Month *"
                  select
                  size="small"
                  sx={{ flex: '1 1 180px' }}
                  error={!!errors.old_futures_month}
                  helperText={errors.old_futures_month?.message}
                >
                  <MenuItem value="">— Chưa chọn —</MenuItem>
                  {futuresMonths.map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              control={control}
              name="new_futures_month"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="New Futures Month *"
                  select
                  size="small"
                  sx={{ flex: '1 1 180px' }}
                  error={!!errors.new_futures_month}
                  helperText={errors.new_futures_month?.message}
                >
                  <MenuItem value="">— Chưa chọn —</MenuItem>
                  {futuresMonths.map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Box>

          {/* Row 4: Lot To Roll + Quantity (auto + override) */}
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <Controller
              control={control}
              name="lot_to_roll"
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value === '' ? '' : field.value}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  label="Lot To Roll *"
                  type="number"
                  size="small"
                  sx={{ flex: '1 1 160px' }}
                  error={!!errors.lot_to_roll}
                  helperText={errors.lot_to_roll?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="quantity_kg"
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value === '' ? '' : field.value}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  label="Quantity (kg)"
                  type="number"
                  size="small"
                  disabled={!quantityOverride}
                  sx={{ flex: '1 1 200px' }}
                  helperText={
                    quantityOverride ? 'Tự nhập' : '= lot_to_roll × 60kg (giả định)'
                  }
                />
              )}
            />
            <Controller
              control={control}
              name="quantity_override"
              render={({ field }) => (
                <Box display="flex" alignItems="center" gap={1}>
                  <input
                    type="checkbox"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    style={{ width: 16, height: 16 }}
                  />
                  <span>Tự nhập Quantity</span>
                </Box>
              )}
            />
          </Box>

          {/* Row 5: Spread + Rolling Fee + Old Diffs */}
          <Box display="flex" gap={2} flexWrap="wrap">
            <Controller
              control={control}
              name="spread"
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value === '' ? '' : field.value}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  label="Spread *"
                  type="number"
                  size="small"
                  inputProps={{ step: '0.0001' }}
                  sx={{ flex: '1 1 140px' }}
                  error={!!errors.spread}
                  helperText={errors.spread?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="rolling_fee"
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value === '' ? '' : field.value}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  label="Rolling Fee *"
                  type="number"
                  size="small"
                  inputProps={{ step: '0.0001' }}
                  sx={{ flex: '1 1 140px' }}
                  error={!!errors.rolling_fee}
                  helperText={errors.rolling_fee?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="old_diffs"
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value === '' ? '' : field.value}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  label="Old Diffs"
                  type="number"
                  size="small"
                  inputProps={{ step: '0.0001' }}
                  sx={{ flex: '1 1 140px' }}
                  helperText="Auto từ contract.ptbf_diffs"
                />
              )}
            />
          </Box>

          {/* Row 6: Note */}
          <Controller
            control={control}
            name="note"
            render={({ field }) => (
              <TextField
                {...field}
                label="Ghi chú"
                size="small"
                multiline
                minRows={2}
                error={!!errors.note}
                helperText={errors.note?.message}
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Huỷ
        </Button>
        <Button
          onClick={() => void onSubmit()}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Lưu
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PTBFRollingFormDialog
